import { useEffect, useState } from "react";

/**
 * useMediaQuery — observe a CSS media query from React.
 * Returns true when the query matches. SSR-safe (returns false during render
 * on the server, then re-reads on mount).
 */
const hasMatchMedia = () => typeof window !== "undefined" && typeof window.matchMedia === "function";

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    hasMatchMedia() ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    // No matchMedia (jsdom, very old embedded browsers): treat as not matching,
    // i.e. the full desktop layout, rather than throwing during render.
    if (!hasMatchMedia()) return;
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/** Defaults to phones; the map shell passes its compact-layout breakpoint. */
export const useIsMobile = (maxWidth = 600) => useMediaQuery(`(max-width: ${maxWidth}px)`);
